import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatDate, formatDateTimeShort } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { getCategory } from "../../lib/categories";
import { FileText, CheckCircle2, FileEdit, Clock, Eye, TrendingUp, Plus, Pencil, Trash2, ExternalLink, Search, Inbox, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="meta-label text-slate-400">{label}</span>
      <Icon size={18} style={{ color }} />
    </div>
    <p className="font-headline text-3xl font-black text-[#0F172A]">{value}</p>
  </div>
);

const STATUS_BADGE = {
  published: { label: "Publié", cls: "bg-emerald-100 text-emerald-700" },
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-600" },
  pending: { label: "En attente", cls: "bg-orange-100 text-orange-700" },
  scheduled: { label: "Programmé", cls: "bg-amber-100 text-amber-700" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const canDelete = ["admin", "editor"].includes(user.role);
  const isAdmin = user.role === "admin";

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    const params = {};
    if (search) params.search = search;
    if (filter) params.status = filter;
    api.get("/admin/articles", { params }).then((r) => setArticles(r.data.articles || [])).catch(() => {});
  }, [search, filter]);

  useEffect(() => { document.title = "Tableau de bord — Admin"; }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Article supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const validate = async (id) => {
    try {
      await api.post(`/admin/articles/${id}/validate`);
      toast.success("Article validé et publié ✔");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-5 sm:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-headline text-3xl font-black text-[#0F172A]">Tableau de bord</h1>
          <p className="meta-label text-slate-400 mt-1">Bienvenue, {user.name}</p>
        </div>
        <Link to="/admin/articles/new" data-testid="create-article-button"
          className="inline-flex items-center gap-2 bg-[#E11D48] text-white px-5 py-2.5 rounded-full font-sub text-sm font-semibold hover:bg-[#be123c] transition-colors">
          <Plus size={16} /> Nouvel article
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={FileText} label="Total articles" value={stats.total} color="#0F172A" />
          <StatCard icon={CheckCircle2} label="Publiés" value={stats.published} color="#047857" />
          <StatCard icon={Inbox} label="En attente" value={stats.pending} color="#EA580C" />
          <StatCard icon={FileEdit} label="Brouillons" value={stats.drafts} color="#64748B" />
          <StatCard icon={Clock} label="Programmés" value={stats.scheduled} color="#B45309" />
          <StatCard icon={Eye} label="Vues totales" value={stats.total_views} color="#E11D48" />
        </div>
      )}

      {stats && stats.most_viewed?.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-8">
          <h2 className="font-headline text-lg font-black text-[#0F172A] flex items-center gap-2 mb-4"><TrendingUp size={18} className="text-[#E11D48]" /> Articles les plus consultés</h2>
          <div className="space-y-2">
            {stats.most_viewed.map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 text-sm">
                <span className="font-headline font-black text-slate-300 w-6">{i + 1}</span>
                <span className="font-sub flex-1 truncate text-[#1E293B]">{a.title}</span>
                <span className="meta-label text-slate-400 flex items-center gap-1"><Eye size={12} />{a.views}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Management table */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="p-4 border-b border-[#E2E8F0] flex flex-wrap items-center gap-3">
          <h2 className="font-headline text-lg font-black text-[#0F172A] mr-auto">Gestion des articles</h2>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…"
              data-testid="admin-article-search" className="pl-9 w-48 font-sub text-sm" />
          </div>
          <div className="flex gap-1">
            {[["", "Tous"], ["pending", "En attente"], ["published", "Publiés"], ["draft", "Brouillons"], ["scheduled", "Programmés"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v || "all"}`}
                className={`meta-label px-3 py-1.5 rounded ${filter === v ? "bg-[#0F172A] text-white" : "text-slate-500 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
        </div>

        {articles.length === 0 ? (
          <div className="p-12 text-center">
            <p className="font-body text-slate-500 mb-4">Aucun article pour le moment.</p>
            <Link to="/admin/articles/new" className="meta-label text-[#E11D48]">+ Créer votre premier article</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="meta-label text-slate-400 border-b border-[#E2E8F0]">
                  <th className="px-4 py-3 font-medium">Titre</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Rubrique</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Date</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Vues</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => {
                  const badge = STATUS_BADGE[a.status] || STATUS_BADGE.draft;
                  const cat = getCategory(a.category);
                  return (
                    <tr key={a.id} className="border-b border-[#F1F5F9] hover:bg-slate-50" data-testid={`admin-row-${a.slug}`}>
                      <td className="px-4 py-3">
                        <p className="font-sub text-sm font-semibold text-[#0F172A] line-clamp-1 max-w-xs">{a.title}</p>
                        <p className="meta-label text-slate-400">{a.author}{a.is_featured ? " · À la une" : ""}</p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell"><span className="meta-label" style={{ color: cat.color }}>{cat.name}</span></td>
                      <td className="px-4 py-3"><span className={`meta-label px-2 py-1 rounded ${badge.cls}`}>{badge.label}</span></td>
                      <td className="px-4 py-3 hidden md:table-cell meta-label text-slate-500">
                        {a.status === "scheduled" ? (
                          <span className="inline-flex items-center gap-1 text-amber-700" title="Date de parution programmée">
                            <Clock size={12} /> {formatDateTimeShort(a.published_at)}
                          </span>
                        ) : formatDate(a.published_at || a.updated_at)}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell meta-label text-slate-500">{a.views}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isAdmin && a.status === "pending" && (
                            <button onClick={() => validate(a.id)} title="Valider et publier" data-testid={`validate-${a.slug}`}
                              className="w-8 h-8 flex items-center justify-center rounded hover:bg-emerald-100 text-emerald-600"><Check size={16} /></button>
                          )}
                          {a.status !== "draft" && a.status !== "pending" && (
                            <a href={`/${a.category}/${a.slug}`} target="_blank" rel="noreferrer" title="Voir" data-testid={`view-${a.slug}`}
                              className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"><ExternalLink size={15} /></a>
                          )}
                          <button onClick={() => navigate(`/admin/articles/${a.id}/edit`)} title="Modifier" data-testid={`edit-${a.slug}`}
                            className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"><Pencil size={15} /></button>
                          {canDelete && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button title="Supprimer" data-testid={`delete-${a.slug}`}
                                  className="w-8 h-8 flex items-center justify-center rounded hover:bg-rose-100 text-rose-500"><Trash2 size={15} /></button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
                                  <AlertDialogDescription>« {a.title} » sera définitivement supprimé. Cette action est irréversible.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => del(a.id)} data-testid={`confirm-delete-${a.slug}`} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
